import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideDrawer, Box, BodyText, PartnerAccountPermissionResponse } from 'vopay';
import useFetchPartnerAccoutPermissionFields from '@/hooks/useApi/useFetchPartnerAccoutPermissionFields';
import usePostPartnerAccountPermissions from '@/hooks/useApi/usePostPartnerAccountPermissions';
import usePartnerBearer from '@/hooks/useApi/usePartnerBearer';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerAccounts } from '@/providers/PartnerAccounts.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AccountContext from '@/components/Accounts/AccountContext';
import { Button } from 'UI';
import AccountPermissionsForm, { AccountPermissionsFormRef } from './AccountPermissionsForm';
import styles from './AccountPermissionsDrawer.module.scss';

type AccountPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  accountName?: string;
};

type PermissionFormValues = Record<string, string | boolean>;

type FC = FunctionComponent<AccountPermissionsDrawerProps>;

const AccountPermissionsDrawer: FC = ({ isOpen, onClose, accountName }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Permissions' });
  const { accountId } = useParams<{ accountId: string }>();
  const { details } = useContext(AccountContext);
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const partnerBearer = usePartnerBearer(details[accountId]?.PartnerAccountID ?? '');
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, PartnerAccountPermissionResponse[]>>({});
  const [isLoading, setIsLoadingLocal] = useState(false);
  const [, setGlobalLoading] = useDataFetchIndicator();
  const [permissionsResponse, postPermissions] = usePostPartnerAccountPermissions({ bearer: partnerBearer });
  const { fetchAccountPermissionFields } = useFetchPartnerAccoutPermissionFields(partnerBearer);
  const { refreshPartnerAccounts } = usePartnerAccounts();
  const formRef = useRef<AccountPermissionsFormRef | null>(null);
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const setLoading = (val: boolean) => {
    setIsLoadingLocal(val);
    setGlobalLoading(val);
  };

  const groupAndSortPermissions = (
    fields: PartnerAccountPermissionResponse[],
  ): Record<string, PartnerAccountPermissionResponse[]> => {
    const grouped = fields.reduce<Record<string, PartnerAccountPermissionResponse[]>>((acc, field) => {
      const section = field.Section || 'other';
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    }, {});

    Object.keys(grouped).forEach((section) => {
      grouped[section].sort((a, b) => {
        const getPriority = (type: string) => {
          switch (type) {
            case 'boolean':
              return 4;
            case 'number':
              return 3;
            case 'string':
            case 'text':
              return 2;
            default:
              return 0;
          }
        };
        const pA = getPriority(a.Type);
        const pB = getPriority(b.Type);
        if (pA !== pB) return pB - pA;
        return a.Label.localeCompare(b.Label);
      });
    });

    return grouped;
  };

  useEffect(() => {
    if (!isOpen) {
      setGroupedPermissions({});
      return;
    }

    if (!partnerBearer) return;

    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const response = await fetchAccountPermissionFields({ VopayAccountID: accountId });
        if (response?.Success) {
          const fields: PartnerAccountPermissionResponse[] = Array.isArray(response.PermissionValues)
            ? response.PermissionValues
            : Object.values(response.PermissionValues);
          setGroupedPermissions(groupAndSortPermissions(fields));
        }
      } catch (error) {
        alertDialog.error(t('Errors.title'), t('Errors.fetchFailed'));
        console.error('Error fetching account permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [isOpen, accountId, partnerBearer]);

  useEffect(() => {
    if (permissionsResponse.success) {
      setLoading(false);
      refreshPartnerAccounts({ VopayAccountID: accountId });
      onClose();
      toast.success(t('Success.updated'));
    }
    if (permissionsResponse.errorMessage) {
      setLoading(false);
      alertDialog.error(t('Errors.title'), permissionsResponse.errorMessage);
    }
  }, [permissionsResponse]);

  const handleSave = (values: PermissionFormValues) => {
    setLoading(true);
    postPermissions({ VopayAccountID: accountId, ...values });
  };

  const handleEnableAll = () => {
    formRef.current?.enableAll();
  };

  const hasPermissions = Object.keys(groupedPermissions).length > 0;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col">
          <span>{t('Drawer.title')}</span>
          {accountName && <BodyText weight="md">{accountName}</BodyText>}
        </Box>
      }
      footer={
        <Box jc="fs" gap="sm" className={styles.footer}>
          {!isRoleViewOnly && !isSearching && (
            <Button onClick={handleEnableAll} colorScheme="secondary" className={styles.enableAllButton}>
              {t('Drawer.enableAll')}
            </Button>
          )}
          {!isRoleViewOnly && (
            <Button
              disabled={isLoading || !hasChanges}
              colorScheme="primary"
              className={styles.saveButton}
              form="accountPermissionsForm"
              type="submit"
            >
              {t('Drawer.save')}
            </Button>
          )}
        </Box>
      }
    >
      {!isLoading && hasPermissions && (
        <AccountPermissionsForm
          ref={formRef}
          permissionRows={groupedPermissions}
          onSave={handleSave}
          onFormStateChange={(changed) => setHasChanges(changed)}
          onSearchChange={setIsSearching}
          isReadOnly={isRoleViewOnly}
        />
      )}
    </SideDrawer>
  );
};

export default AccountPermissionsDrawer;
